import React, { useState } from 'react';
import { Plus, User, Pencil } from 'lucide-react';
import Modal from '../../../shared/components/ui/Modal';
import { ApplicantModal } from '../forms/ApplicantModal';
import QuickApplicantModal from './QuickApplicantModal';

const MAX_APPLICANTS = 5;

const DealApplicantsSection = ({ dealId, applicants = [], onUpdated }) => {
  const [showFullModal, setShowFullModal] = useState(false);
  const [quickApplicantNumber, setQuickApplicantNumber] = useState(null);

  const nextNumber =
    applicants.length > 0
      ? Math.min(
          MAX_APPLICANTS,
          Math.max(...applicants.map((a) => a.applicant_number || 1)) + 1
        )
      : 1;

  const canAddMore = applicants.length < MAX_APPLICANTS;

  const openAdd = () => {
    if (applicants.length === 0 || nextNumber <= 2) {
      setShowFullModal(true);
      return;
    }
    setQuickApplicantNumber(nextNumber);
  };

  return (
    <section className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-4 py-3 sm:px-5">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Applicants</h3>
          <p className="mt-0.5 text-xs text-gray-500">
            Buyer and co-applicants for agreements (up to {MAX_APPLICANTS}).
          </p>
        </div>
        {canAddMore && (
          <button
            type="button"
            onClick={openAdd}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#2A565E] px-3 py-2 text-xs font-semibold text-white hover:bg-[#234952]">
            <Plus className="h-3.5 w-3.5" />
            {applicants.length === 0 ? 'Add applicant' : 'Add co-applicant'}
          </button>
        )}
      </div>

      <div className="p-4 sm:p-5">
        {!applicants.length ? (
          <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/60 px-4 py-8 text-center">
            <User className="mx-auto h-8 w-8 text-gray-400" />
            <p className="mt-2 text-sm font-medium text-gray-800">No applicants yet</p>
            <p className="mt-1 text-xs text-gray-500">
              Add the primary buyer first — then co-applicants if needed.
            </p>
          </div>
        ) : (
          <ul className="grid gap-3 sm:grid-cols-2">
            {applicants.map((app) => (
              <li
                key={app.id ?? app.applicant_number}
                className="rounded-xl border border-gray-200 bg-gray-50/40 p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-wide text-teal-800">
                      {app.applicant_number === 1
                        ? 'Primary buyer'
                        : `Co-applicant ${app.applicant_number - 1}`}
                    </p>
                    <p className="mt-1 text-base font-semibold text-gray-900">
                      {app.name || '—'}
                    </p>
                    <p className="mt-1 text-sm text-gray-600">
                      {app.mobile || app.email || '—'}
                    </p>
                    {(app.pan || app.aadhar) && (
                      <p className="mt-2 text-xs text-gray-500">
                        {app.pan ? `PAN ${app.pan}` : ''}
                        {app.pan && app.aadhar ? ' · ' : ''}
                        {app.aadhar ? `Aadhaar ${app.aadhar}` : ''}
                      </p>
                    )}
                  </div>
                  {app.applicant_number <= 2 && (
                    <button
                      type="button"
                      title="Edit in full form"
                      onClick={() => setShowFullModal(true)}
                      className="rounded-lg border border-gray-200 bg-white p-2 text-gray-600 hover:bg-gray-50">
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {showFullModal && (
        <Modal name="Application form" width={70} onClose={() => setShowFullModal(false)}>
          <ApplicantModal
            dealId={dealId}
            onClose={() => {
              setShowFullModal(false);
              onUpdated?.();
            }}
          />
        </Modal>
      )}

      {quickApplicantNumber != null && (
        <Modal
          name={`Co-applicant ${quickApplicantNumber - 1}`}
          width={50}
          onClose={() => setQuickApplicantNumber(null)}>
          <QuickApplicantModal
            dealId={dealId}
            applicantNumber={quickApplicantNumber}
            onClose={() => setQuickApplicantNumber(null)}
            onSuccess={() => {
              setQuickApplicantNumber(null);
              onUpdated?.();
            }}
          />
        </Modal>
      )}
    </section>
  );
};

export default DealApplicantsSection;
